import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { 
  Trophy, 
  Star, 
  ExternalLink, 
  MapPin, 
  Heart,
  Building2,
  ArrowRight,
  Sparkles,
  CheckCircle,
  Handshake,
  Globe2
} from 'lucide-react';
import { EloTemperatureBadge } from './EloVoteButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NGO of the Month Spotlight Component
 * Displays the featured rescue organization on the homepage
 * Supports global and regional spotlights
 */
const NgoOfTheMonth = ({ className = '', region = null }) => {
  const navigate = useNavigate();
  const [ngo, setNgo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchFeaturedNgo = async () => {
      try {
        const url = region 
          ? `${API_URL}/api/adoption-agencies/featured/ngo-of-the-month?region=${encodeURIComponent(region)}`
          : `${API_URL}/api/adoption-agencies/featured/ngo-of-the-month`;
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setNgo(data);
        }
      } catch (error) {
        console.error('Error fetching NGO of the Month:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedNgo();
  }, [region]);

  if (loading) {
    return (
      <Card className={`overflow-hidden ${className}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <Skeleton className="w-20 h-20 rounded-xl" />
            <div className="flex-1">
              <Skeleton className="h-6 w-48 mb-2" />
              <Skeleton className="h-4 w-32 mb-2" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!ngo) {
    return null;
  }

  const isPartner = ngo.status === 'partner';
  const isChapter = ngo.org_structure === 'chapter';
  const showRegionalBadge = region || ngo.featured_region;

  return (
    <Card 
      className={`overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 ${className}`}
      data-testid="ngo-of-the-month"
    >
      <CardContent className="p-0">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-3 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-white" />
          <span className="font-semibold text-white">
            {showRegionalBadge ? `${ngo.featured_region || region} NGO of the Month` : 'NGO of the Month'}
          </span>
          {showRegionalBadge && <Globe2 className="w-4 h-4 text-amber-200" />}
          <Sparkles className="w-4 h-4 text-amber-200 ml-auto" />
        </div>

        <div className="p-6">
          <div className="flex items-start gap-4">
            {/* Logo/Icon */}
            {ngo.logo_url && !imageError ? (
              <img 
                src={ngo.logo_url} 
                alt={ngo.name} 
                className="w-20 h-20 rounded-xl object-cover border-2 border-amber-200"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center border-2 border-amber-200">
                <Building2 className="w-10 h-10 text-amber-600" />
              </div>
            )}

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-bold text-lg text-gray-900 truncate">
                  {ngo.name}
                </h3>
                {isPartner && (
                  <Badge className="bg-amber-500 text-white text-xs">
                    <Handshake className="w-3 h-3 mr-1" />
                    Partner
                  </Badge>
                )}
                {isChapter && ngo.parent_org_name && (
                  <Badge variant="outline" className="text-amber-700 border-amber-400 text-xs">
                    {ngo.parent_org_name.split(' ')[0]} Chapter
                  </Badge>
                )}
                {ngo.verified && (
                  <Badge variant="outline" className="text-green-600 border-green-500 text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>

              {/* Location */}
              {(ngo.city || ngo.state) && (
                <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                  <MapPin className="w-3 h-3" />
                  {ngo.city && ngo.state ? `${ngo.city}, ${ngo.state}` : ngo.state || ngo.country}
                </div>
              )}

              {/* Description */}
              <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                {ngo.description}
              </p>

              {/* Stats Row */}
              <div className="flex items-center gap-4 flex-wrap">
                {ngo.elo_score && (
                  <EloTemperatureBadge score={ngo.elo_score} />
                )}
                {ngo.is_no_kill && (
                  <Badge variant="secondary" className="text-xs">
                    <Heart className="w-3 h-3 mr-1 text-rose-500" />
                    No-Kill
                  </Badge>
                )}
                {ngo.featured_reason && (
                  <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    {ngo.featured_reason}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 mt-4 pt-4 border-t border-amber-200">
            {ngo.website && (
              <Button 
                size="sm" 
                className="bg-amber-500 hover:bg-amber-600 text-white"
                asChild
              >
                <a href={ngo.website} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Visit Website
                </a>
              </Button>
            )}
            <Button 
              size="sm" 
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => navigate('/adoption-agencies')}
            >
              View All NGOs
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NgoOfTheMonth;
